import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Briefcase, DollarSign, Clock, AlertTriangle, ExternalLink, Info } from "lucide-react";

import { api, formatApiError, getActiveBranchName } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { money } from "@/lib/constants";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

function KpiCard({ label, value, icon: Icon, testId }) {
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="text-2xl font-semibold mono mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function ProductividadTecnicos() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const canView = hasPermission("analytics:view");

  const [data, setData] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branchId, setBranchId] = useState("all");
  const [techId, setTechId] = useState("all");
  const [drillOpen, setDrillOpen] = useState(false);
  const [drill, setDrill] = useState(null);

  const tenantParam = useMemo(
    () => (isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}),
    [isSuperAdmin, activeTenantId],
  );

  const params = useMemo(() => ({
    ...tenantParam,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    branch_id: branchId !== "all" ? branchId : undefined,
    technician_id: techId !== "all" ? techId : undefined,
  }), [tenantParam, dateFrom, dateTo, branchId, techId]);

  async function fetchData() {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/reports/technician-productivity", { params }),
        technicians.length === 0
          ? api.get("/users", { params: { ...tenantParam, role: "tecnico" } })
          : Promise.resolve({ data: technicians }),
      ]);
      setData(r1.data);
      if (technicians.length === 0) {
        setTechnicians(Array.isArray(r2.data) ? r2.data : (r2.data?.items || []));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, branchId, techId, activeTenantId]);

  async function openDrilldown(tech) {
    if (!tech || tech.technician_id === "__unassigned__") return;
    setDrill({ header: tech, items: null, loading: true });
    setDrillOpen(true);
    try {
      const { data: r } = await api.get(
        `/reports/technician-productivity/${tech.technician_id}`,
        { params: { ...tenantParam,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          branch_id: branchId !== "all" ? branchId : undefined,
        } },
      );
      setDrill({ header: tech, items: r.items || [], loading: false });
    } catch (err) {
      toast.error(formatApiError(err));
      setDrill(null); setDrillOpen(false);
    }
  }

  if (!canView) {
    return (
      <div className="p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>No tienes permisos para ver este reporte.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const items = data?.items || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="page-technician-productivity">
      <PageHeader
        title="Productividad por técnico"
        description="Base operativa de medición. No calcula bonos, destajo ni comisiones. Reglas de atribución explícitas para no engañar."
        actions={
          <ShareExportPanel
            reportKey="technician_productivity"
            reportName="Productividad por técnico"
            endpoint="/reports/technician-productivity"
            params={params}
            exportPermission="analytics:export"
            filename="productividad-tecnicos"
            branchId={branchId !== "all" ? branchId : null}
            filters={{
              date_from: dateFrom || null,
              date_to: dateTo || null,
              branch_name: getActiveBranchName(),
            }}
            stats={{
              total_technicians: totals.total_technicians,
              total_assigned: totals.total_assigned,
              total_completed: totals.total_completed,
              total_stalled: totals.total_stalled,
              total_generated_value: totals.total_generated_value,
              row_count: items.length,
            }}
            testIdPrefix="tp-share"
          />
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="tp-date-from" placeholder="Desde" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="tp-date-to" placeholder="Hasta" />
          <BranchFilter />
          <Select value={techId} onValueChange={setTechId}>
            <SelectTrigger data-testid="tp-tech"><SelectValue placeholder="Técnico" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {technicians.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Técnicos" value={totals.total_technicians ?? 0} icon={Users} testId="tp-kpi-tecs" />
        <KpiCard label="Asignadas" value={totals.total_assigned ?? 0} icon={Briefcase} testId="tp-kpi-assigned" />
        <KpiCard label="Cerradas" value={totals.total_completed ?? 0} icon={Briefcase} testId="tp-kpi-completed" />
        <KpiCard label="Detenidas" value={totals.total_stalled ?? 0} icon={AlertTriangle} testId="tp-kpi-stalled" />
        <KpiCard label="Valor generado" value={money(totals.total_generated_value)} icon={DollarSign} testId="tp-kpi-value" />
      </div>

      {data?.disclaimer && (
        <Alert data-testid="tp-disclaimer">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">{data.disclaimer}</AlertDescription>
        </Alert>
      )}

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Sin datos en el rango seleccionado.</div>
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Técnico</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="text-right">Asignadas</TableHead>
                <TableHead className="text-right">Activas</TableHead>
                <TableHead className="text-right">Cerradas</TableHead>
                <TableHead className="text-right">Detenidas</TableHead>
                <TableHead className="text-right">Prom h</TableHead>
                <TableHead className="text-right">Carga hoy</TableHead>
                <TableHead className="text-right">Tasa cierre</TableHead>
                <TableHead className="text-right">Valor generado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => {
                const isUnassigned = t.technician_id === "__unassigned__";
                return (
                  <TableRow
                    key={t.technician_id}
                    className={`tbl-row ${isUnassigned ? "bg-slate-50" : "cursor-pointer"}`}
                    onClick={() => !isUnassigned && openDrilldown(t)}
                    data-testid={`tp-row-${t.technician_id}`}
                  >
                    <TableCell className="text-sm font-medium">
                      {t.technician_name}
                      {t.has_shared_orders && (
                        <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-800">Compartida</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{t.branch_name || "—"}</TableCell>
                    <TableCell className="text-right mono">{t.assigned}</TableCell>
                    <TableCell className="text-right mono">{t.active}</TableCell>
                    <TableCell className="text-right mono">{t.completed}</TableCell>
                    <TableCell className={`text-right mono ${t.stalled > 0 ? "text-amber-700 font-medium" : ""}`}>
                      {t.stalled}
                    </TableCell>
                    <TableCell className="text-right mono text-xs">
                      {t.avg_hours != null ? t.avg_hours : <span className="text-muted-foreground" title="Sin actual_hours reportadas">N/A</span>}
                    </TableCell>
                    <TableCell className="text-right mono">{t.current_load}</TableCell>
                    <TableCell className="text-right mono text-xs">
                      {t.close_rate != null
                        ? `${(t.close_rate * 100).toFixed(0)}%`
                        : <span className="text-muted-foreground" title="Muestra insuficiente (< 3 asignadas)">—</span>}
                    </TableCell>
                    <TableCell className="text-right mono">{money(t.generated_value)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Drill-down */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tp-drilldown">
          <DialogHeader>
            <DialogTitle>{drill?.header?.technician_name}</DialogTitle>
            <DialogDescription>
              {drill?.header?.branch_name || "—"} · {drill?.items?.length ?? 0} órdenes
            </DialogDescription>
          </DialogHeader>
          {drill?.loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (drill?.items || []).length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">Sin órdenes.</div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Folio</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead>Comp.</TableHead>
                  <TableHead className="text-right">Horas</TableHead>
                  <TableHead className="text-right">Venta</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(drill?.items || []).map((it) => (
                  <TableRow key={it.service_order_id} data-testid={`tp-drill-row-${it.service_order_id}`}>
                    <TableCell className="mono text-xs">{it.folio}</TableCell>
                    <TableCell className="text-xs">{it.status}</TableCell>
                    <TableCell>
                      {it.shared_with_other_technicians && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800">
                          {it.total_technicians_on_order} téc.
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right mono text-xs">
                      {it.actual_hours_this_tech ?? <span className="text-muted-foreground">N/A</span>}
                    </TableCell>
                    <TableCell className="text-right mono text-xs">{money(it.sale_total)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/ordenes/${it.service_order_id}`, "_blank")} data-testid={`tp-drill-goto-${it.service_order_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
